import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, Mail, MapPin, CheckCircle, ArrowRight, Star, Clock, Shield, Leaf } from 'lucide-react';
import { LOCATIONS } from '../data/locations';
import { useLocationProjects } from '../hooks/useLocationProjects';
import './LocationServicePage.css';

// Service-Typen mit erweiterten Keywords
const SERVICES = {
  'gartenpflege': {
    name: 'Gartenpflege',
    title: 'Professionelle Gartenpflege',
    description: 'Rasenmähen, Heckenschnitt, Beetpflege und mehr',
    keywords: [
      // Haupt-Keywords
      'Gartenpflege', 'Gartenservice', 'Gärtner', 'Gartenhilfe', 'Gartenarbeiten', 'GaLaBau', 'Landschaftsgärtner', 'Gartenbau',
      // Rasen
      'Rasenmähen', 'Rasenpflege', 'Rasen mähen', 'Rasen vertikutieren', 'Vertikutieren', 'Rasen düngen', 'Rasendünger', 'Rasen aerifizieren', 'Rasen nachsäen', 'Rollrasen verlegen', 'Rollrasen', 'Rasen anlegen', 'Rasenkanten schneiden', 'Mähroboter Service',
      // Hecken
      'Heckenschnitt', 'Hecken schneiden', 'Hecke schneiden', 'Formschnitt', 'Buchsbaum schneiden', 'Thuja schneiden', 'Liguster schneiden', 'Heckenpflege',
      // Beete & Pflanzen
      'Beetpflege', 'Beete anlegen', 'Unkraut entfernen', 'Unkrautbekämpfung', 'Jäten', 'Mulchen', 'Pflanzarbeiten', 'Stauden pflanzen', 'Blumen pflanzen', 'Gehölzschnitt',
      // Sträucher & Bäume
      'Strauchschnitt', 'Sträucher schneiden', 'Obstbaumschnitt', 'Obstbäume schneiden', 'Ziersträucher schneiden', 'Rosen schneiden', 'Rosenschnitt',
      // Saisonal
      'Frühjahrsputz Garten', 'Herbstputz Garten', 'Laub entfernen', 'Laubbeseitigung', 'Winterdienst Garten', 'Frostschutz Pflanzen',
      // Allgemein
      'Grünpflege', 'Grundstückspflege', 'Außenanlagenpflege', 'Objektpflege', 'Dauerpflege Garten', 'Gartenpflegevertrag', 'regelmäßige Gartenpflege'
    ],
    features: [
      'Regelmäßige Rasenpflege & Rasenmähen',
      'Professioneller Heckenschnitt',
      'Beetpflege & Unkrautentfernung',
      'Baum- und Strauchschnitt',
      'Laubbeseitigung im Herbst',
      'Frühjahrs- und Herbstputz'
    ]
  },
  'erdarbeiten': {
    name: 'Erdarbeiten',
    title: 'Erdarbeiten & Baggerarbeiten',
    description: 'Aushub, Planieren, Fundamentarbeiten und Geländemodellierung',
    keywords: [
      // Haupt-Keywords
      'Erdarbeiten', 'Baggerarbeiten', 'Tiefbau', 'Erdbau', 'Erdarbeiter',
      // Bagger/Maschinen
      'Minibagger', 'Minibagger mieten', 'Minibagger mit Fahrer', 'Bagger mieten', 'Kleinbagger', 'Kompaktbagger', 'Radlader',
      // Aushub
      'Aushub', 'Erdaushub', 'Baugrube ausheben', 'Baugrubenaushub', 'Kelleraushub', 'Aushubarbeiten',
      // Fundament
      'Fundamentarbeiten', 'Fundament ausheben', 'Fundament graben', 'Streifenfundament', 'Punktfundament', 'Bodenplatte vorbereiten',
      // Drainage/Entwässerung
      'Drainage verlegen', 'Drainage legen', 'Drainagerohr verlegen', 'Entwässerung Garten', 'Sickergrube', 'Rigole', 'Versickerung',
      // Gräben
      'Kabelgraben', 'Leitungsgraben', 'Graben ausheben', 'Rohrgraben', 'Stromkabel verlegen Graben', 'Wasserleitung Graben',
      // Pool/Teich
      'Teichaushub', 'Teich ausheben', 'Teich anlegen', 'Gartenteich bauen', 'Poolaushub', 'Pool graben', 'Schwimmteich ausheben', 'Naturpool anlegen',
      // Gelände
      'Geländemodellierung', 'Planierarbeiten', 'Grundstück planieren', 'Gelände begradigen', 'Hang abtragen', 'Böschung anlegen', 'Erdreich verteilen', 'Gelände nivellieren',
      // Boden
      'Bodenaustausch', 'Mutterboden', 'Mutterboden abtragen', 'Oberboden abtragen', 'Erde abtragen', 'Erdreich entsorgen', 'Boden verdichten',
      // Wurzeln/Stubben
      'Wurzelstock entfernen', 'Wurzel ausgraben', 'Baumwurzel entfernen', 'Stubben ausgraben',
      // Pflaster/Terrasse Vorbereitung
      'Pflasterarbeiten Vorbereitung', 'Unterbau Pflaster', 'Terrasse Erdarbeiten', 'Einfahrt Unterbau', 'Hofeinfahrt vorbereiten', 'Wegebau Unterbau',
      // Garten allgemein
      'Gartengestaltung Erdarbeiten', 'Garten anlegen Erdarbeiten', 'Rasen anlegen Erdarbeiten', 'Rollrasen Vorbereitung',
      // Gewerblich
      'Erdarbeiten Gewerbe', 'Erdarbeiten Baustelle', 'Erdarbeiten Neubau', 'Erschließung Grundstück',
      // Spezial
      'Zisterne einbauen', 'Regenwasserzisterne', 'Versenkregner installieren', 'Beregnungsanlage verlegen', 'Gartenbewässerung verlegen'
    ],
    features: [
      'Aushubarbeiten für Fundamente & Keller',
      'Geländemodellierung & Planierarbeiten',
      'Kabelgräben & Leitungsgräben',
      'Pool- und Teichaushub',
      'Drainage & Entwässerung',
      'Wurzelstockentfernung',
      'Bodenaustausch & Mutterboden',
      'Pflaster- & Terrassen-Unterbau'
    ]
  },
  'baumfaellung': {
    name: 'Baumfällung',
    title: 'Baumfällung & Baumpflege',
    description: 'Sichere Baumfällung, Baumpflege und Stubbenfräsen',
    keywords: [
      // Haupt-Keywords
      'Baumfällung', 'Baum fällen', 'Baumpflege', 'Baumservice', 'Baumpfleger', 'Baumkletterer', 'Baumdienst',
      // Fällung
      'Baumfällung Kosten', 'Baum fällen Kosten', 'Baum fällen lassen', 'gefährliche Bäume fällen', 'Problemfällung', 'Seilklettertechnik',
      // Schnitt/Pflege
      'Baumschnitt', 'Obstbaumschnitt', 'Obstbaum schneiden', 'Kronenpflege', 'Kroneneinkürzung', 'Kronenrückschnitt', 'Lichtraumprofil', 'Formschnitt Baum',
      // Totholz
      'Totholz entfernen', 'Totholzentfernung', 'abgestorbene Äste entfernen',
      // Stubben
      'Stubbenfräsen', 'Stubben fräsen', 'Stubbenentfernung', 'Baumstumpf entfernen', 'Wurzelstock fräsen', 'Wurzelentfernung',
      // Notfall
      'Sturmschaden Baum', 'Sturmschadenbeseitigung', 'umgestürzter Baum', 'Notfällung', 'Notfall Baumfällung',
      // Gutachten
      'Baumgutachten', 'Baumkontrolle', 'Baumbeurteilung', 'kranker Baum'
    ],
    features: [
      'Sichere Baumfällung aller Größen',
      'Kronenpflege & Kroneneinkürzung',
      'Totholzentfernung',
      'Stubbenfräsen & Wurzelentfernung',
      'Notfall-Baumfällung bei Sturmschäden',
      'Baumgutachten & Beratung'
    ]
  },
  'hochdruckreinigung': {
    name: 'Hochdruckreinigung',
    title: 'Professionelle Hochdruckreinigung',
    description: 'Reinigung von Terrassen, Einfahrten, Fassaden und mehr',
    keywords: [
      // Haupt-Keywords
      'Hochdruckreinigung', 'Hochdruckreiniger', 'Dampfreinigung', 'Reinigungsservice',
      // Terrasse
      'Terrassenreinigung', 'Terrasse reinigen', 'Terrasse reinigen lassen', 'Holzterrasse reinigen', 'WPC Terrasse reinigen', 'Steinplatten reinigen',
      // Pflaster
      'Pflasterreinigung', 'Pflastersteine reinigen', 'Verbundsteine reinigen', 'Betonpflaster reinigen', 'Natursteinpflaster reinigen',
      // Einfahrt
      'Einfahrt reinigen', 'Garageneinfahrt reinigen', 'Hofeinfahrt reinigen', 'Auffahrt reinigen',
      // Fassade
      'Fassadenreinigung', 'Hausfassade reinigen', 'Klinkerfassade reinigen', 'Putzfassade reinigen',
      // Algen/Moos
      'Algen entfernen', 'Moos entfernen', 'Grünbelag entfernen', 'Grünspan entfernen', 'Flechten entfernen', 'schwarze Flecken entfernen',
      // Sonstiges
      'Steinmauer reinigen', 'Gartenmauer reinigen', 'Zaun reinigen', 'Poolbereich reinigen', 'Gehweg reinigen', 'Treppe reinigen'
    ],
    features: [
      'Terrassenreinigung (Holz, Stein, WPC)',
      'Einfahrten & Pflastersteine reinigen',
      'Fassadenreinigung',
      'Mauer- und Zaunreinigung',
      'Poolbereich reinigen',
      'Garageneinfahrten säubern'
    ]
  },
  'entsorgung': {
    name: 'Gartenbau',
    title: 'Gartenbau & Gartengestaltung',
    description: 'Gartengestaltung, Grünschnittentsorgung und Gartenabfallbeseitigung',
    keywords: [
      // Haupt-Keywords
      'Gartenbau', 'Gartengestaltung', 'Gartenanlage', 'Garten anlegen', 'Garten gestalten',
      'Grünschnittentsorgung', 'Grünschnitt entsorgen', 'Gartenabfall entsorgen', 'Gartenabfallentsorgung', 'Grünabfall',
      // Spezifisch
      'Astwerk entsorgen', 'Äste entsorgen', 'Zweige entsorgen', 'Heckenabfall entsorgen', 'Laub entsorgen', 'Rasenschnitt entsorgen',
      // Holz
      'Baumstamm entsorgen', 'Holz entsorgen', 'Wurzeln entsorgen', 'Stubben entsorgen',
      // Erde
      'Erdaushub entsorgen', 'Erde entsorgen', 'Mutterboden entsorgen', 'Bauschutt Garten',
      // Service
      'Grünabfall abholen', 'Gartenabfall abholen', 'Container Grünschnitt', 'Containerservice', 'Mulde bestellen',
      // Umwelt
      'umweltgerechte Entsorgung', 'Kompostierung', 'Recycling Grünschnitt'
    ],
    features: [
      'Gartengestaltung & Gartenanlage',
      'Grünschnitt & Astwerk abholen',
      'Laub- und Gartenabfallentsorgung',
      'Wurzel- und Stubbentransport',
      'Erdaushub entsorgen',
      'Containerservice auf Anfrage'
    ]
  }
};

// Service-specific FAQs for rich content
const SERVICE_FAQS = {
  'gartenpflege': [
    { q: 'Was kostet Gartenpflege pro Stunde in {ORT}?', a: 'Unsere Stundensätze für Gartenpflege in {ORT} liegen zwischen 35-45 Euro je nach Aufwand. Für regelmäßige Pflegeverträge bieten wir Sonderkonditionen. Kontaktieren Sie uns für ein kostenloses Angebot.' },
    { q: 'Wie oft sollte der Rasen in {ORT} gemäht werden?', a: 'In der Wachstumsperiode (April bis Oktober) empfehlen wir wöchentliches Rasenmähen. Wir bieten flexible Pflegeverträge für regelmäßige Rasenpflege in {ORT} und Umgebung.' },
    { q: 'Bieten Sie auch Heckenschnitt in {ORT} an?', a: 'Ja, wir schneiden alle Arten von Hecken in {ORT}: Thuja, Liguster, Buchsbaum, Hainbuche und mehr. Formschnitt und Rückschnitt gehören zu unseren Kernleistungen.' },
    { q: 'Wann ist die beste Zeit für Gartenpflege?', a: 'Grundsätzlich ganzjährig. Frühjahrsputz im März/April, regelmäßige Pflege April-Oktober, Herbstputz im November. Wir sind das ganze Jahr für Sie in {ORT} im Einsatz.' },
  ],
  'erdarbeiten': [
    { q: 'Was kosten Erdarbeiten mit Minibagger in {ORT}?', a: 'Die Kosten für Baggerarbeiten in {ORT} hängen vom Umfang ab. Unser Minibagger inkl. Fahrer ist stunden- oder tageweise buchbar. Kontaktieren Sie uns für ein Festpreisangebot.' },
    { q: 'Kann ich einen Minibagger mit Fahrer in {ORT} mieten?', a: 'Ja, Sie können unseren Minibagger inklusive erfahrenem Fahrer für Projekte in {ORT} buchen. Ideal für Aushub, Fundamente, Teichbau oder Drainage.' },
    { q: 'Welche Erdarbeiten bieten Sie in {ORT} an?', a: 'Wir führen in {ORT} alle Erdarbeiten durch: Baugrubenaushub, Fundamentarbeiten, Drainage verlegen, Kabelgräben, Pool- und Teichaushub, Geländemodellierung und Bodenaustausch.' },
    { q: 'Entsorgen Sie den Erdaushub auch?', a: 'Ja, wir kümmern uns um die fachgerechte Entsorgung von Erdaushub und Mutterboden in {ORT}. Transport und Entsorgung sind im Angebot enthalten oder werden separat kalkuliert.' },
  ],
  'baumfaellung': [
    { q: 'Was kostet eine Baumfällung in {ORT}?', a: 'Die Kosten für Baumfällung in {ORT} hängen von Größe, Standort und Zugänglichkeit ab. Kleine Bäume ab ca. 150 Euro, größere je nach Aufwand. Wir erstellen nach Besichtigung ein verbindliches Festpreisangebot.' },
    { q: 'Brauche ich eine Genehmigung für Baumfällung in {ORT}?', a: 'Das hängt von der Baumsatzung Ihrer Gemeinde ab. In vielen Orten ist eine Genehmigung erforderlich. Wir beraten Sie gerne und unterstützen bei der Antragstellung in {ORT}.' },
    { q: 'Bieten Sie auch Stubbenfräsen in {ORT} an?', a: 'Ja, nach der Baumfällung können wir den Baumstumpf in {ORT} professionell ausfräsen. So können Sie die Fläche danach wieder nutzen.' },
    { q: 'Was tun bei Sturmschäden an Bäumen in {ORT}?', a: 'Bei Sturmschäden sind wir oft noch am selben Tag vor Ort in {ORT}. Rufen Sie uns an unter 0176-55669090 für Notfall-Baumfällungen.' },
  ],
  'hochdruckreinigung': [
    { q: 'Was kostet Terrassenreinigung in {ORT}?', a: 'Die Kosten für Terrassenreinigung in {ORT} richten sich nach Fläche und Material. Kontaktieren Sie uns für ein kostenloses Angebot. Die Ergebnisse sprechen für sich!' },
    { q: 'Wie oft sollte man Pflastersteine reinigen lassen?', a: 'Wir empfehlen eine professionelle Reinigung alle 1-2 Jahre, je nach Moos- und Algenbefall. In schattigen Bereichen in {ORT} kann häufigere Reinigung sinnvoll sein.' },
    { q: 'Reinigen Sie auch Hausfassaden in {ORT}?', a: 'Ja, wir reinigen Fassaden, Klinker, Putz und andere Oberflächen in {ORT} schonend mit professioneller Hochdrucktechnik.' },
    { q: 'Werden beim Hochdruckreinigen die Fugen beschädigt?', a: 'Nein, wir arbeiten mit professionellem Equipment und angepasstem Druck. Die Fugen bleiben intakt und werden gleichzeitig gereinigt.' },
  ],
  'entsorgung': [
    { q: 'Was kostet Gartenbau in {ORT}?', a: 'Die Kosten für Gartenbau und Gartengestaltung in {ORT} hängen vom Umfang ab. Wir bieten pauschal oder nach Aufwand an. Gerne erstellen wir ein kostenloses Angebot.' },
    { q: 'Holen Sie Gartenabfälle in {ORT} ab?', a: 'Ja, wir holen Grünschnitt, Astwerk, Laub und Gartenabfälle direkt bei Ihnen in {ORT} ab. Bequem und fachgerecht.' },
    { q: 'Bieten Sie Gartengestaltung in {ORT} an?', a: 'Ja, wir gestalten und legen Gärten in {ORT} an. Von der Planung bis zur Umsetzung — alles aus einer Hand.' },
    { q: 'Bieten Sie Containerservice in {ORT} an?', a: 'Auf Anfrage können wir einen Containerservice für größere Mengen Grünschnitt oder Gartenabfälle in {ORT} organisieren.' },
  ],
};

// Seasonal content per service
const SEASONAL_TIPS = {
  'gartenpflege': {
    spring: 'Jetzt ist die beste Zeit für den Frühjahrsputz im Garten! Rasenpflege, Beetvorbereitung und Gehölzschnitt stehen an.',
    summer: 'Regelmäßige Rasenpflege und Heckenschnitt halten Ihren Garten im Sommer in Topform.',
    autumn: 'Herbstputz, Laubbeseitigung und Wintervorbereitung - wir machen Ihren Garten winterfest.',
    winter: 'Planen Sie jetzt Ihre Gartenpflege für das Frühjahr und sichern Sie sich einen Termin.',
  },
  'erdarbeiten': {
    spring: 'Ideale Zeit für Erdarbeiten: Frostfrei und trocken. Fundamente, Drainage und Teichbau jetzt starten!',
    summer: 'Optimale Bedingungen für Baggerarbeiten und Geländemodellierung.',
    autumn: 'Noch rechtzeitig vor dem Winter: Drainage verlegen und Entwässerung sicherstellen.',
    winter: 'Planen Sie Ihre Erdarbeiten für das Frühjahr. Kostenlose Beratung und Angebotserstellung.',
  },
  'baumfaellung': {
    spring: 'Baumpflege und Kronenschnitt im Frühjahr fördern gesundes Wachstum.',
    summer: 'Totholzentfernung und Kronenpflege - für die Sicherheit Ihrer Bäume.',
    autumn: 'Ideale Fällsaison: Oktober bis Februar. Jetzt Termin sichern!',
    winter: 'Hauptsaison für Baumfällung. Nutzen Sie die laubfreie Zeit für optimale Ergebnisse.',
  },
  'hochdruckreinigung': {
    spring: 'Nach dem Winter: Terrassen und Einfahrten von Moos und Algen befreien!',
    summer: 'Ihre Terrasse für den Sommer auffrischen - Hochdruckreinigung mit sofortigem Ergebnis.',
    autumn: 'Vor dem Winter noch schnell die Außenflächen reinigen lassen.',
    winter: 'Im Frühjahr steht die Reinigungssaison an - jetzt vorplanen.',
  },
  'entsorgung': {
    spring: 'Frühjahrsputz im Garten? Wir entsorgen Ihren Grünschnitt fachgerecht.',
    summer: 'Heckenschnitt und Rasenschnitt - wir nehmen alles mit.',
    autumn: 'Laubberge und Gartenabfälle? Wir räumen auf und entsorgen alles.',
    winter: 'Baumschnitt und Sturmholz - auch im Winter zuverlässig entsorgt.',
  },
};

const LocationServicePage = ({ service: serviceProp }) => {
  const { location } = useParams();
  const service = serviceProp || 'gartenpflege';
  
  const locationData = LOCATIONS[location];
  const serviceData = SERVICES[service];

  // Echte, bereits erledigte Projekte aus diesem Ort (inkl. seiner Ortsteile).
  // Das ist der einzige Inhalt, der diese Seite von den anderen Ortsseiten
  // wirklich unterscheidet - deshalb steht er weit oben.
  const { projects: localProjects } = useLocationProjects(location, 3);
  
  const pageTitle = locationData && serviceData 
    ? `${serviceData.name} in ${locationData.name} | Nessenius Garten & Landschaftsbau`
    : 'Seite nicht gefunden | Nessenius Garten & Landschaftsbau';
  const pageDescription = locationData && serviceData
    ? `${serviceData.title} in ${locationData.name} und Umgebung. Ihr lokaler Partner für ${serviceData.description}. Festpreise. Schnelle Termine. Kostenlose Beratung. Jetzt anrufen: 0176-55669090`
    : '';
  
  const pageKeywords = locationData && serviceData
    ? [
        `${serviceData.name} ${locationData.name}`,
        `${serviceData.name} ${locationData.region || 'Göttingen'}`,
        `Gärtner ${locationData.name}`,
        `GaLaBau ${locationData.name}`,
        ...serviceData.keywords.slice(0, 10).map(k => `${k} ${locationData.name}`)
      ].join(', ')
    : '';

  // Get current season for seasonal content
  const currentSeason = useMemo(() => {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'autumn';
    return 'winter';
  }, []);

  // Get nearby locations sorted by distance proximity
  const nearbyLocations = useMemo(() => {
    if (!locationData) return [];
    return Object.entries(LOCATIONS)
      .filter(([key, loc]) => key !== location && loc.type !== 'Ortsteil')
      .sort((a, b) => Math.abs(a[1].distance - locationData.distance) - Math.abs(b[1].distance - locationData.distance))
      .slice(0, 10);
  }, [location, locationData]);

  // Ortsteile des aktuellen Ortes - werden nur als Text genannt, nicht verlinkt.
  // Eigene Ortsteil-Seiten wuerden nahezu identische Klonseiten erzeugen und
  // werden per 301 auf diese Seite weitergeleitet (siehe scripts/generate-seo-files.mjs).
  const nearbyOrtsteile = useMemo(() => {
    if (!locationData) return [];
    return Object.entries(LOCATIONS)
      .filter(([key, loc]) =>
        key !== location &&
        loc.type === 'Ortsteil' &&
        loc.region === locationData.name
      )
      .sort((a, b) => a[1].distance - b[1].distance)
      .slice(0, 12);
  }, [location, locationData]);

  // Build FAQ data with location name
  const faqs = useMemo(() => {
    if (!locationData || !service) return [];
    const faqTemplates = SERVICE_FAQS[service] || [];
    return faqTemplates.map(f => ({
      q: f.q.replace(/{ORT}/g, locationData.name),
      a: f.a.replace(/{ORT}/g, locationData.name),
    }));
  }, [service, locationData]);
  
  // Update document title, meta tags, and inject JSON-LD
  React.useEffect(() => {
    document.title = pageTitle;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && pageDescription) {
      metaDesc.setAttribute('content', pageDescription);
    }
    
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    if (pageKeywords) {
      metaKeywords.setAttribute('content', pageKeywords);
    }
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://nessenius-galabau.de/${service}/${location}`;
    
    let geoRegion = document.querySelector('meta[name="geo.region"]');
    if (!geoRegion) {
      geoRegion = document.createElement('meta');
      geoRegion.name = 'geo.region';
      document.head.appendChild(geoRegion);
    }
    geoRegion.content = 'DE-NI';
    
    let geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (!geoPlacename) {
      geoPlacename = document.createElement('meta');
      geoPlacename.name = 'geo.placename';
      document.head.appendChild(geoPlacename);
    }
    if (locationData) {
      geoPlacename.content = locationData.name;
    }

    // OG tags for social sharing
    const ogTags = {
      'og:title': pageTitle,
      'og:description': pageDescription,
      'og:url': `https://nessenius-galabau.de/${service}/${location}`,
      'og:type': 'website',
      'og:locale': 'de_DE',
    };
    Object.entries(ogTags).forEach(([prop, content]) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', prop);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    
    // Inject dynamic JSON-LD structured data
    const existingScripts = document.querySelectorAll('script[data-dynamic-jsonld]');
    existingScripts.forEach(s => s.remove());
    
    if (locationData && serviceData) {
      // 1. BreadcrumbList Schema
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://nessenius-galabau.de/" },
          { "@type": "ListItem", "position": 2, "name": "Leistungen", "item": "https://nessenius-galabau.de/leistungen" },
          { "@type": "ListItem", "position": 3, "name": serviceData.name, "item": `https://nessenius-galabau.de/leistungen/${service === 'erdarbeiten' ? 'baggerarbeiten' : service}` },
          { "@type": "ListItem", "position": 4, "name": `${serviceData.name} in ${locationData.name}`, "item": `https://nessenius-galabau.de/${service}/${location}` }
        ]
      };

      // 2. Service Schema with location
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": serviceData.title,
        "provider": {
          "@type": "LocalBusiness",
          "name": "Nessenius Garten & Landschaftsbau",
          "telephone": "+49 176 55669090",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Unter den Höfen 11",
            "addressLocality": "Nörten-Hardenberg",
            "postalCode": "37176",
            "addressRegion": "Niedersachsen",
            "addressCountry": "DE"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "47",
            "bestRating": "5"
          }
        },
        "areaServed": {
          "@type": "Place",
          "name": locationData.name,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": locationData.name,
            "addressRegion": "Niedersachsen",
            "addressCountry": "DE"
          }
        },
        "description": `${serviceData.title} in ${locationData.name} und Umgebung. ${serviceData.description}`,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "areaServed": locationData.name
        }
      };

      // 3. FAQ Schema
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      };

      [breadcrumbSchema, serviceSchema, faqSchema].forEach(schema => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-dynamic-jsonld', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-dynamic-jsonld]');
      scripts.forEach(s => s.remove());
    };
  }, [pageTitle, pageDescription, pageKeywords, service, location, locationData, serviceData, faqs]);
  
  if (!locationData || !serviceData) {
    return (
      <div className="location-page not-found">
        <div className="container">
          <h1>Seite nicht gefunden</h1>
          <p>Der gewünschte Ort oder Service wurde nicht gefunden.</p>
          <Link to="/" className="btn-primary" data-testid="back-home-btn">Zur Startseite</Link>
        </div>
      </div>
    );
  }

  const seasonalTip = SEASONAL_TIPS[service]?.[currentSeason] || '';

  return (
    <div className="location-service-page" data-testid="location-service-page">

      {/* Hero Section */}
      <section className="location-hero">
        <div className="container">
          <div className="hero-badge">{locationData.region}</div>
          <h1>{serviceData.name} in {locationData.name}</h1>
          <p className="hero-subtitle">
            Ihr zuverlässiger Partner für {serviceData.description.toLowerCase()} in {locationData.name} und der gesamten Region {locationData.region}.
            {locationData.distance > 0 && ` Nur ${locationData.distance} km von unserem Standort entfernt.`}
          </p>
          <div className="hero-trust-row">
            <span className="hero-trust-item"><Star size={16} /> 4.9 von 5 Sternen</span>
            <span className="hero-trust-item"><Clock size={16} /> Termine in 1-2 Wochen</span>
            <span className="hero-trust-item"><Shield size={16} /> Festpreisgarantie</span>
          </div>
          <div className="hero-cta">
            <a href="tel:+4917655669090" className="btn-primary" data-testid="hero-call-btn">
              <Phone size={18} />
              Jetzt anrufen
            </a>
            <Link to="/kontakt" className="btn-secondary" data-testid="hero-contact-btn">
              Kostenloses Angebot
            </Link>
          </div>
        </div>
      </section>

      {/* Seasonal Tip */}
      {seasonalTip && (
        <section className="seasonal-tip-section">
          <div className="container">
            <div className="seasonal-tip">
              <Leaf size={20} />
              <p><strong>Aktueller Tipp:</strong> {seasonalTip}</p>
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      <section className="location-services">
        <div className="container">
          <h2>Unsere {serviceData.name}-Leistungen in {locationData.name}</h2>
          <p className="section-intro">
            Als lokaler Fachbetrieb bieten wir in {locationData.name} und dem gesamten {locationData.region} ein umfangreiches Leistungsspektrum im Bereich {serviceData.name}:
          </p>
          <div className="services-grid">
            {serviceData.features.map((feature, index) => (
              <div key={index} className="service-feature">
                <CheckCircle size={24} className="check-icon" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="location-why-us">
        <div className="container">
          <h2>Warum Nessenius Garten & Landschaftsbau in {locationData.name}?</h2>
          <div className="why-us-grid">
            <div className="why-us-item">
              <div className="why-us-icon"><MapPin size={32} /></div>
              <h3>Lokal & Schnell</h3>
              <p>Wir sind in {locationData.name} und Umgebung tätig und können kurzfristig vor Ort sein.{locationData.distance > 0 ? ` Nur ${locationData.distance} km Anfahrt.` : ' Direkt vor Ort.'}</p>
            </div>
            <div className="why-us-item">
              <div className="why-us-icon"><Star size={32} /></div>
              <h3>4.9 Sterne Bewertung</h3>
              <p>Unsere Kunden in der Region bewerten uns mit 4.9 von 5 Sternen. Überzeugen Sie sich selbst!</p>
            </div>
            <div className="why-us-item">
              <div className="why-us-icon"><Shield size={32} /></div>
              <h3>Festpreise & Transparenz</h3>
              <p>Verbindliche Festpreisangebote nach kostenloser Besichtigung. Keine versteckten Kosten.</p>
            </div>
            <div className="why-us-item">
              <div className="why-us-icon"><Phone size={32} /></div>
              <h3>Persönliche Beratung</h3>
              <p>Kostenlose Vor-Ort-Beratung und individuelle Angebote für Ihr Projekt in {locationData.name}.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="location-faq">
          <div className="container">
            <h2>Häufige Fragen zu {serviceData.name} in {locationData.name}</h2>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <details key={index} className="faq-item" data-testid={`faq-item-${index}`}>
                  <summary className="faq-question">{faq.q}</summary>
                  <p className="faq-answer">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="location-cta">
        <div className="container">
          <h2>{serviceData.name} in {locationData.name} anfragen</h2>
          <p>Kontaktieren Sie uns für ein kostenloses und unverbindliches Angebot!</p>
          <div className="cta-buttons">
            <a href="tel:+4917655669090" className="btn-primary" data-testid="cta-call-btn">
              <Phone size={18} />
              0176 - 556 690 90
            </a>
            <a href="mailto:info@nessenius-galabau.de" className="btn-secondary" data-testid="cta-email-btn">
              <Mail size={18} />
              E-Mail schreiben
            </a>
          </div>
          <div className="cta-info">
            <p><strong>Nessenius Garten & Landschaftsbau</strong></p>
            <p>Unter den Höfen 11, 37176 Nörten-Hardenberg</p>
          </div>
        </div>
      </section>

      {/* Related Services Section - Internal Linking */}
      <section className="location-related-services">
        <div className="container">
          <h2>Weitere Leistungen in {locationData.name}</h2>
          <p>Wir bieten in {locationData.name} und Umgebung auch folgende Dienstleistungen an:</p>
          <div className="related-services-grid">
            {Object.entries(SERVICES)
              .filter(([key]) => key !== service)
              .map(([key, svc]) => (
                <Link 
                  key={key}
                  to={`/${key}/${location}`} 
                  className="related-service-card"
                  data-testid={`related-service-${key}`}
                >
                  <h3>{svc.name} in {locationData.name}</h3>
                  <p>{svc.description}</p>
                  <span className="related-service-link">Mehr erfahren <ArrowRight size={14} /></span>
                </Link>
              ))
            }
          </div>
        </div>
      </section>

      {/* Nearby Cities - Internal Linking */}
      <section className="location-nearby">
        <div className="container">
          <h2>{serviceData.name} in der Nähe von {locationData.name}</h2>
          <div className="nearby-locations-list">
            {nearbyLocations.map(([key, loc]) => (
              <Link 
                key={key}
                to={`/${service}/${key}`}
                className="nearby-location-link"
              >
                {serviceData.name} {loc.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Echte Projekte aus diesem Ort - einzigartiger Inhalt pro Seite */}
      {localProjects.length > 0 && (
        <section className="location-projects">
          <div className="container">
            <h2>Unsere Projekte in {locationData.name}</h2>
            <p className="section-intro-sm">
              Arbeiten, die wir in {locationData.name} und Umgebung bereits ausgeführt haben:
            </p>
            <div className="location-projects-grid">
              {localProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projekte/${project.slug}`}
                  className="location-project-card"
                  data-testid={`location-project-${project.slug}`}
                >
                  {project.mainImage && (
                    <div className="location-project-image">
                      <img
                        src={project.mainImage}
                        alt={`${project.title} – ${serviceData.name} in ${locationData.name}`}
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="location-project-body">
                    <span className="location-project-place">
                      <MapPin size={14} /> {project.location}
                    </span>
                    <h3>{project.title}</h3>
                    {project.excerpt && <p>{project.excerpt}</p>}
                    <span className="location-project-link">
                      Projekt ansehen <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ortsteile - bewusst nur als Textnennung, nicht verlinkt */}
      {nearbyOrtsteile.length > 0 && (
        <section className="other-locations">
          <div className="container">
            <h2>{serviceData.name} in {locationData.name} und Umgebung</h2>
            <p className="section-intro-sm">
              {serviceData.name} bieten wir in ganz {locationData.name} an –
              inklusive der Ortsteile {nearbyOrtsteile.map(([, loc]) => loc.name).join(', ')}.
            </p>
          </div>
        </section>
      )}

      {/* SEO Text Block */}
      <section className="seo-text-section">
        <div className="container">
          <h2>{serviceData.title} in {locationData.name} - {locationData.region}</h2>
          <div className="seo-text">
            <p>
              Sie suchen einen zuverlässigen Fachbetrieb für {serviceData.name.toLowerCase()} in {locationData.name}? 
              <strong> Nessenius Garten & Landschaftsbau</strong> ist Ihr lokaler Partner für professionelle {serviceData.description.toLowerCase()} in {locationData.name} und der gesamten Region {locationData.region}. 
              Von unserem Standort in Nörten-Hardenberg aus sind wir {locationData.distance > 0 ? `in nur ${locationData.distance} km bei Ihnen` : 'direkt vor Ort'}.
            </p>
            <p>
              Ob {serviceData.keywords.slice(0, 5).join(', ')} - wir bieten Ihnen das komplette Leistungsspektrum. 
              Unsere Kunden bewerten uns mit 4.9 von 5 Sternen. Profitieren Sie von unserer Erfahrung und kontaktieren Sie uns noch heute für eine kostenlose Beratung in {locationData.name}.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationServicePage;
